import {
  AccountData,
  Conversation,
  Follower,
  FollowerMembershipState,
} from "@markit/common.types";
import { Colors } from "../../utils/colors";
import ProfilePic from "../ProfilePic";
import { TimeAgo } from "../../utils/TimeAgo";
import ConversationItemDropdownSettings from "../CreatorDashboard/Conversations/ConversationItemDropdownSettings";
import { CSSProperties, useMemo } from "react";
import { isUnreadConversation } from "../../redux/slices/conversationSlice";
import { getAccountState } from "../../redux/slices/accountSlice";
import { useSelector } from "react-redux";
import { Icon } from "@iconify/react";

type ConversationItemProps = {
  item: Conversation;
  participant: AccountData | undefined;
  followerData: Follower | undefined;
  isSelectedItem?: boolean;
  isHovered?: boolean;
  isOpenDropdown?: boolean;
  setIsOpenDropdown?: (isOpen: boolean) => void;
  contentContainerStyles?: CSSProperties;
  onLocalConversationUpdate?: (conversation: Conversation) => void;
};

export const ConversationItem = (props: ConversationItemProps) => {
  const {
    item,
    participant,
    followerData,
    isSelectedItem,
    isHovered,
    isOpenDropdown,
    setIsOpenDropdown,
    contentContainerStyles,
    onLocalConversationUpdate,
  } = props;
  const { uid } = useSelector(getAccountState).account.accountData;

  const isUnread = useMemo(
    () => isUnreadConversation(item.chatIndicator, uid),
    [item, uid]
  );

  return (
    <div className="AlignedRow" style={{ gap: 10 }}>
      <div>
        <ProfilePic
          user={participant}
          size={48}
          gradientBackground
          forcePointer
        />
      </div>
      <div
        className="ColumnNormal"
        style={{
          gap: 2,
          height: 61,
          width: 299,
          position: "relative",
          ...contentContainerStyles,
        }}
      >
        <div className="AlignedRowSpaced" style={{ gap: 10 }}>
          <div className="AlignedRow" style={{ gap: 4 }}>
            <span
              className="sectionTitle OneLineText"
              style={{
                color: isSelectedItem ? Colors.WHITE : Colors.BLACK,
              }}
            >
              {participant ? participant.fullName : "•••"}
            </span>
            {followerData &&
            followerData.membershipState === FollowerMembershipState.ACTIVE ? (
              <Icon
                icon="ion:ribbon"
                height={14}
                style={{
                  color: isSelectedItem ? Colors.WHITE : Colors.BLACK,
                }}
              />
            ) : null}
          </div>
          {!isHovered && item.recentTimestamp !== undefined ? (
            <span
              className="bodySubtext"
              style={{
                color: isSelectedItem ? Colors.WHITE : Colors.GRAY1,
                whiteSpace: "nowrap",
              }}
            >
              {TimeAgo(item.recentTimestamp)}
            </span>
          ) : null}
        </div>
        <div
          className="AlignedRowSpaced"
          style={{ gap: 14, alignItems: "flex-start" }}
        >
          <span
            className="TwoLineText bodySubtext"
            style={{
              color: isSelectedItem ? Colors.WHITE : Colors.GRAY1,
              width: isHovered || isUnread ? 259 : undefined,
            }}
          >
            {item.recentMessage.text
              ? item.recentMessage.text.trim() === ""
                ? "Attachment: 1 Media" // assume if no text that it's media
                : item.recentMessage.text
              : "No message responses yet"}
          </span>
          {isHovered &&
          isOpenDropdown !== undefined &&
          setIsOpenDropdown !== undefined ? (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                right: 0,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <ConversationItemDropdownSettings
                conversationItem={item}
                isOpenDropdown={isOpenDropdown}
                setIsOpenDropdown={setIsOpenDropdown}
                placement="bottom"
                onLocalConversationUpdate={onLocalConversationUpdate}
              />
            </div>
          ) : isUnread ? (
            <div
              style={{
                position: "absolute",
                right: 0,
                top: "50%",
                transform: "translateY(-50%)",
                width: 11,
                height: 11,
                backgroundColor: isSelectedItem ? Colors.WHITE : Colors.BLUE5,
                borderRadius: 64,
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};
